import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

/**
 * Componente de cámara reutilizable.
 * Muestra el video de la webcam y expone el elemento <video> vía ref.
 *
 * Props:
 *   onReady(videoEl) — llamado cuando la cámara está lista
 *   onError(msg)     — llamado si hay error al acceder a la cámara
 *   width / height   — dimensiones del video (default 320x240)
 *   mirror           — voltear horizontalmente (default true, más natural para el usuario)
 */
const FaceCamera = forwardRef(function FaceCamera(
  { onReady, onError, width = 320, height = 240, mirror = true },
  ref
) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Exponer el videoElement hacia arriba via ref
  useImperativeHandle(ref, () => ({
    get video() { return videoRef.current; },
  }));

  useEffect(() => {
    let active = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: width }, height: { ideal: height }, facingMode: 'user' },
          audio: false,
        });

        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            setCameraActive(true);
            onReady && onReady(videoRef.current);
          };
        }
      } catch (err) {
        console.error('Error accediendo a la cámara:', err);
        const msg = err.name === 'NotAllowedError'
          ? 'Permiso de cámara denegado. Actívalo en la configuración del navegador.'
          : 'No se pudo acceder a la cámara.';
        onError && onError(msg);
      }
    }

    startCamera();

    return () => {
      active = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setCameraActive(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="face-camera-wrap" style={{ position: 'relative', display: 'inline-block' }}>
      <video
        ref={videoRef}
        width={width}
        height={height}
        muted
        playsInline
        style={{
          borderRadius: 12,
          transform: mirror ? 'scaleX(-1)' : 'none',
          display: 'block',
          background: '#111',
        }}
      />
      {!cameraActive && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#111', borderRadius: 12, color: '#888', fontSize: 13,
        }}>
          <span>Iniciando cámara...</span>
        </div>
      )}
    </div>
  );
});

export default FaceCamera;
